import React, { useEffect, useState } from "react"
import { Button } from "components/Button"
import { Buttons, Card } from "components/Card/Card"
import { cdn } from "core/cdn"
import AudioTest from "./AudioTest"
import VideoTest from "./VideoTest"
import { languages } from "locales/languages"
import { useDispatch } from "react-redux"
import surveyActions from "containers/Survey/services/actions"
import Alert from "components/Alert/Alert"
import { useLocation, useNavigate } from "react-router"
import { HOME_PAGE, SURVEY_PAGE } from "core/constants"
import { Steps } from "components/Steps"
import { Diagnosis } from "containers/Diagnosis/Diagnosis"
import { hasBothMediaPermissionsGranted } from "containers/Diagnosis/utils"

import PreviousButton from "components/Previous/PreviousButton"

import { useStepper } from "components/Steps/hooks"

import { HOME_PHARMA_DIAGNOSTIC_NAV_STATE } from "containers/Home/Home"

export const step: {
  [x: string]: "default" | "check-video" | "check-micro" | "device-error" | "diagnostic-completed"
} = {
  DEFAULT: "default",
  VIDEO: "check-video",
  AUDIO: "check-micro",
  ERROR: "device-error",
  DIAGNOSTIC_COMPLETED: "diagnostic-completed",
}

const DevicesDiagnostic: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const location = useLocation()
  const fromHomePharmaDiagnostic = Boolean(
    (location.state as typeof HOME_PHARMA_DIAGNOSTIC_NAV_STATE | null)
      ?.fromHomePharmaDiagnostic,
  )
  const [pharmaPreDiagnosisDone, setPharmaPreDiagnosisDone] = useState(
    () => !fromHomePharmaDiagnostic,
  )
  const [, setPharmaPreDiagnosisPermCheck] = useState<
    "checking" | "done"
  >(() => (fromHomePharmaDiagnostic ? "checking" : "done"))

  useEffect(() => {
    if (!fromHomePharmaDiagnostic) return
    let cancelled = false
    hasBothMediaPermissionsGranted()
      .then((bothGranted) => {
        if (cancelled) return
        if (bothGranted) setPharmaPreDiagnosisDone(true)
        setPharmaPreDiagnosisPermCheck("done")
      })
      .catch(() => {
        if (cancelled) return
        setPharmaPreDiagnosisPermCheck("done")
      })
    return () => {
      cancelled = true
    }
  }, [fromHomePharmaDiagnostic])

  const exitDiagnostic = (surveyNav: "previous" | "next") => {
    if (fromHomePharmaDiagnostic) {
      setCurrentStep(step.DIAGNOSTIC_COMPLETED)
      return
    }

    if (surveyNav === "previous") dispatch(surveyActions.previous())
    if (surveyNav === "next") dispatch(surveyActions.next())
    navigate(SURVEY_PAGE)
  }

  // start audio test when on AudioTest and deviceId changes
  const [currentStep, setCurrentStep] = useState<
    "default" | "check-video" | "check-micro" | "device-error" | "diagnostic-completed"
  >("default")
  const [error, setError] = useState<{ video: boolean; audio: boolean }>({
    video: false,
    audio: false,
  })
  const getDevicesInsideStep = () => {
    switch (currentStep) {
      case step.DEFAULT:
        return 1
      case step.VIDEO:
        return 2
      case step.AUDIO:
        return 3
      case step.DIAGNOSTIC_COMPLETED:
        return 4
      case step.ERROR:
      default:
        return 3
    }
  }

  let { current, totalSteps } = useStepper()

  const insideStep = getDevicesInsideStep()
  const totalInsideSteps = 3

  const stepsElement =
    !fromHomePharmaDiagnostic ? (
      <Steps
        current={current}
        insideStep={insideStep}
        totalInsideSteps={totalInsideSteps}
        totalSteps={totalSteps}
      />
    ) : null

  if (fromHomePharmaDiagnostic && !pharmaPreDiagnosisDone) {
    return (
      <Diagnosis
        onFinish={() => setPharmaPreDiagnosisDone(true)}
        onStop={() => navigate(HOME_PAGE, { replace: true })}
      />
    )
  }

  if (currentStep === step.DEFAULT) {
    return (
      <>
        {stepsElement}
        <PreviousButton
          onClick={() => {
            if (fromHomePharmaDiagnostic) {
              navigate(HOME_PAGE, { replace: true })
              return
            }
            exitDiagnostic("previous")
          }}
        />
        <Card
          title={languages.testTheHardware}
          subtitle={languages.beReadyForYourConsultation}
        >
          <div
            className="flex-center"
            style={{ marginTop: "25px", gap: "40px" }}
          >
            <img
              src={cdn("/images/devices/diagnostic/camera.svg")}
              alt="camera"
            />
            <img
              src={cdn("/images/devices/diagnostic/micro.svg")}
              alt="micro"
            />
          </div>
          <div className="flex-center" style={{ marginTop: "50px" }}>
            <Button
              type="primary"
              onClick={() => {
                setCurrentStep(step.VIDEO)
              }}
            >
              {languages.testTheHardware}
            </Button>
          </div>
        </Card>
      </>
    )
  }
  if (currentStep === step.VIDEO) {
    return (
      <>
        {stepsElement}
        <PreviousButton onClick={() => setCurrentStep(step.DEFAULT)} />
        <VideoTest
          onAccept={() => setCurrentStep(step.AUDIO)}
          onError={(errorMessage) => {
            console.error(errorMessage)
            setError({ ...error, video: true })
            setCurrentStep(step.AUDIO)
          }}
        />
      </>
    )
  }
  if (currentStep === step.AUDIO) {
    return (
      <>
        {stepsElement}
        <PreviousButton onClick={() => setCurrentStep(step.VIDEO)} />
        <AudioTest
          onAccept={() => {
            if (error.video) {
              setCurrentStep(step.ERROR)
            } else {
              exitDiagnostic("next")
            }
          }}
          onError={(errorMessage) => {
            console.error(errorMessage)
            setError({ ...error, audio: true })
            setCurrentStep(step.ERROR)
          }}
        />
      </>
    )
  }

  if (currentStep === step.ERROR) {
    return (
      <>
        {stepsElement}
        <PreviousButton onClick={() => setCurrentStep(step.AUDIO)} />
        <Card title={languages.youDetectedAnIssue}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: "50px",
              }}
            >
              {error.video && (
                <img
                  width={120}
                  src={cdn("/images/devices/diagnostic/camera_error.svg")}
                  alt="camera"
                />
              )}
              {error.audio && (
                <img
                  width={120}
                  src={cdn("/images/devices/diagnostic/microphone_error.svg")}
                  alt="audio"
                />
              )}
              <span
                style={{
                  color: "#211452",
                  fontSize: "38px",
                  marginTop: "45px",
                }}
              >
                {error.video && error.audio ? (
                  <>
                    {languages.cameraAndMicDoesNotWork}{" "}
                    <p>{languages.pleaseContactTheReferent}</p>
                  </>
                ) : error.video ? (
                  <>
                    {languages.cameraDoesNotWork}{" "}
                    <p>{languages.pleaseContactTheReferent}</p>
                  </>
                ) : (
                  <>
                    {languages.microDoesNotWork}{" "}
                    <p>{languages.pleaseContactTheReferent}</p>
                  </>
                )}
              </span>
            </div>
            <br />
            <Alert
              type="info"
              style={{ height: "auto" }}
              description={
                <>
                  {languages.pharmacyPleaseDo}
                  <ul>
                    <li>{languages.verifyAccessTo(error)}</li>
                    <li>{languages.restartDevice(error)}</li>
                  </ul>
                  {languages.ifYouNeedHelp}
                  {" "} <b>{languages.atSupportNumber?.replace("au", "")}</b>
                </>
              }
            />
          </div>
          <Buttons direction="vertical">
            <Button
              type="primary"
              onClick={() => {
                setError({ video: false, audio: false })
                setCurrentStep(step.VIDEO)
              }}
            >
              {languages.restartDiagnostic}
            </Button>
            <div style={{ marginTop: "40px" }}></div>
            <span
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                color: "#1D71B8",
                zIndex: 1,
                position: "relative",
              }}
              onClick={() => {
                console.error(
                  `user ignored issue with ${error.video && "the video device"} ${error.audio && "the audio device"}`,
                  {
                    route: "device:ignoreIssues",
                  },
                )
                exitDiagnostic("next")
              }}
            >
              {fromHomePharmaDiagnostic ? "" : languages.continueAnyway}
            </span>
          </Buttons>
        </Card>
      </>
    )
  }
  if (currentStep === step.DIAGNOSTIC_COMPLETED) {
    return (
      <>
        {stepsElement}
        <PreviousButton
          onClick={() => {
            setCurrentStep(step.AUDIO)
          }}
        />
        <Card
          title={languages.diagnosticCompleted}
          style={{ gap: "150px" }}
        >
          <div className="flex-center">
            <img
              src={cdn("/icons/camera_ok.svg")}
              alt="camera ok"
            />
            <img
              src={cdn("/icons/micro_ok.svg")}
              alt="micro ok"
            />
            <p style={{ fontSize: "38px", fontWeight: 400, textAlign: "left", marginLeft: "40px" }}>
              {languages.microphoneAndCameraWorkCorrectly}
            </p>
          </div>
          <div
            className="flex-center"
            style={{ marginTop: "150px" }}
          >
            <Button
              type="default"
              onClick={() => {
                navigate(HOME_PAGE, { replace: true })
              }}
            >
              {languages.completeDiagnostic}
            </Button>
          </div>
        </Card>
      </>
    )
  }
}

export default DevicesDiagnostic
